import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useReducer } from 'react';
import type { UploadState } from '~/components/UploadDialog';
import { assert, exhausted } from '~/lib/assert';

export type UploadListener = (name: string, state: UploadState) => void;

export interface UploadContext {
  readonly listeners: UploadListener[];
  readonly dialogOpen: boolean;
  readonly folderId?: number;
  /** Where focus goes when the dialog closes: the dialog is mounted globally, not by its opener. */
  readonly returnFocus?: HTMLElement | null;
  /** Files dropped outside the dialog, for it to start uploading as it opens. */
  readonly files?: readonly File[];
}

export type UploadAction =
  | { type: 'addListener' | 'removeListener'; listener: UploadListener }
  | {
      type: 'setDialogOpen';
      dialogOpen: boolean;
      folderId?: number;
      returnFocus?: HTMLElement | null;
      files?: readonly File[];
    }
  | { type: 'clearReturnFocus' | 'clearFiles' };

const uploadReducer = (state: UploadContext, action: UploadAction) => {
  switch (action.type) {
    case 'addListener':
      return { ...state, listeners: [...state.listeners, action.listener] };
    case 'removeListener':
      return {
        ...state,
        listeners: state.listeners.filter((listener) => listener !== action.listener),
      };
    case 'setDialogOpen':
      return {
        ...state,
        dialogOpen: action.dialogOpen,
        folderId: action.folderId,
        files: action.files,
        ...(action.dialogOpen && { returnFocus: action.returnFocus }),
      };
    case 'clearReturnFocus':
      return { ...state, returnFocus: undefined };
    case 'clearFiles':
      return { ...state, files: undefined };
    default:
      return exhausted(action);
  }
};

const UploadStateContext = createContext<UploadContext | undefined>(undefined);

const UploadDispatchContext = createContext<Dispatch<UploadAction> | undefined>(undefined);

export interface UploadProviderProps {
  children: ReactNode;
}

export const UploadProvider = ({ children }: UploadProviderProps) => {
  const [state, dispatch] = useReducer(uploadReducer, { listeners: [], dialogOpen: false });

  return (
    <UploadStateContext.Provider value={state}>
      <UploadDispatchContext.Provider value={dispatch}>{children}</UploadDispatchContext.Provider>
    </UploadStateContext.Provider>
  );
};

export const useUploadState = () => {
  const context = useContext(UploadStateContext);
  assert(context !== undefined, 'useUploadState must be used within an UploadProvider');
  return context;
};

export const useUploadDispatch = () => {
  const context = useContext(UploadDispatchContext);
  assert(context !== undefined, 'useUploadDispatch must be used within an UploadProvider');
  return context;
};
